const { useId, useRef, useState } = require('react');
const Styled = require('styled-components').default;
const { Button, Dialog, DialogContent, DialogTitle } = require('@mui/material');

module.exports = DiagnosticsPanel;

const internals = {};

function DiagnosticsPanel({ title = 'Diagnostics', sections = [], events = [], actions = [], onRefresh,
    summary, checks, instructions, autoRefreshOnOpen = false }) {

    const { Launcher, PanelDialog, Header, Toolbar, Controls, ViewNavigation, Search, Content, Section, Data, EventList,
        ResultCard, CheckList, Check, Badge, Disclosure, EventHeading, statusLabel, friendlyTitle, formatTime, copyText } = internals;
    const [open, setOpen] = useState(false);
    const [pending, setPending] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const defaultView = summary || checks?.length || instructions?.length ? 'overview' : events.length ? 'activity' : 'technical';
    const [view, setView] = useState(defaultView);
    const [search, setSearch] = useState('');
    const running = useRef(false);
    const contentRef = useRef(null);
    const titleId = useId();
    const newestEvents = events.map((event, index) => ({ event, index }))
        .sort((left, right) => {

            const difference = new Date(right.event.at).getTime() - new Date(left.event.at).getTime();
            return Number.isFinite(difference) && difference !== 0 ? difference : right.index - left.index;
        });
    const query = search.trim().toLowerCase();
    const filteredEvents = newestEvents.filter(({ event }) => !query || [event.title, event.detail,
        event.event, event.source, JSON.stringify(event.details)].some((value) => String(value || '').toLowerCase().includes(query)));

    const run = async (label, callback, success) => {

        if (running.current) {
            return;
        }

        running.current = true;
        setPending(true);
        setFeedback(null);
        let timeout;
        try {
            await Promise.race([
                callback(),
                new Promise((resolve, reject) => {

                    timeout = setTimeout(() => reject(new Error('Operation timed out. Please try again.')), 10000);
                })
            ]);
            setFeedback({ message: success || `${label} completed.`, error: false });
        }
        catch (error) {
            setFeedback({ message: `${label} failed${error?.message ? `: ${error.message}` : '.'}`, error: true });
        }
        finally {
            clearTimeout(timeout);
            running.current = false;
            setPending(false);
        }
    };

    const openPanel = () => {

        setView(defaultView);
        setSearch('');
        setOpen(true);
        if (autoRefreshOnOpen && onRefresh) {
            run('Refresh', onRefresh);
        }
    };

    const selectView = (nextView) => {

        setView(nextView);
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
    };

    const copyPayload = { title, sections, events,
        ...(summary === undefined ? {} : { summary }),
        ...(checks === undefined ? {} : { checks }),
        ...(instructions === undefined ? {} : { instructions }) };

    return <>
        <Launcher variant='contained' onClick={openPanel} aria-haspopup='dialog' aria-expanded={open}>
            Diagnostics
        </Launcher>
        <PanelDialog open={open} onClose={() => setOpen(false)} aria-labelledby={titleId} fullWidth maxWidth='md'>
            <Header>
                <DialogTitle id={titleId}>{title}</DialogTitle>
                <Button aria-label='Close diagnostics' onClick={() => setOpen(false)}>Close</Button>
            </Header>
            <Toolbar>
                <Controls>
                    {onRefresh && <Button variant='outlined' disabled={pending} onClick={() => run('Refresh', onRefresh)}>Refresh</Button>}
                    <Button variant='contained' disabled={pending} onClick={() => run('Copy diagnostics',
                        () => copyText(JSON.stringify(copyPayload, null, 2), contentRef.current), 'Diagnostics copied.')}>
                        Copy diagnostics
                    </Button>
                    {actions.map(({ id, label, onClick }) => <Button key={id} disabled={pending}
                        onClick={() => run(label, onClick)}>{label}</Button>)}
                </Controls>
                <ViewNavigation aria-label='Diagnostics views'>
                    <button type='button' aria-pressed={view === 'overview'} onClick={() => selectView('overview')}>Overview</button>
                    <button type='button' aria-pressed={view === 'activity'} onClick={() => selectView('activity')}>Activity ({events.length})</button>
                    <button type='button' aria-pressed={view === 'technical'} onClick={() => selectView('technical')}>Technical</button>
                </ViewNavigation>
                {feedback && <p role={feedback.error ? 'alert' : 'status'}>{feedback.message}</p>}
            </Toolbar>
            <DialogContent dividers ref={contentRef}>
                <Content aria-busy={pending}>
                    {view === 'overview' && <>
                    {summary && <ResultCard $status={summary.status}>
                        <Badge $status={summary.status}>{statusLabel(summary.status)}</Badge>
                        <h3>{summary.title}</h3>
                        <p>{summary.message}</p>
                    </ResultCard>}
                    {checks?.length > 0 && <Section>
                        <h3>Status checks</h3>
                        <CheckList>{checks.map(({ id, label, status, detail }) => <Check key={id}>
                            <EventHeading><h4>{label}</h4><Badge $status={status}>{statusLabel(status)}</Badge></EventHeading>
                            {detail && <p>{detail}</p>}
                        </Check>)}</CheckList>
                    </Section>}
                    {instructions?.length > 0 && <Section>
                        <h3>How to test</h3>
                        <ol>{instructions.map((step, index) => <li key={index}>{step}</li>)}</ol>
                    </Section>}
                    {!summary && !checks?.length && !instructions?.length && <p>No overview is available. Choose Activity or Technical to inspect diagnostics.</p>}
                    </>}
                    {view === 'activity' && <Section>
                        <EventHeading><h3>Recent activity</h3><span>Newest first</span></EventHeading>
                        <Search><span>Search activity</span><input type='search' value={search}
                            onChange={(event) => setSearch(event.target.value)} placeholder='Search events or details' /></Search>
                        <p aria-live='polite'>{filteredEvents.length} of {events.length} events</p>
                        {events.length === 0 ? <p>No activity recorded yet. Run a test to see what happens here.</p>
                            : filteredEvents.length === 0 ? <p>No activity matches your search. Try another word or clear the search.</p> : <EventList>
                            {filteredEvents.map(({ event: { at, source, event, details, title: eventTitle, detail, status }, index }) => <li key={index}>
                                <EventHeading><h4>{eventTitle || friendlyTitle(event)}</h4>
                                    <time dateTime={String(at)} title={String(at)}>{formatTime(at)}</time></EventHeading>
                                {status && <Badge $status={status}>{statusLabel(status)}</Badge>}
                                {detail && <p>{detail}</p>}
                                <Disclosure>
                                    <summary>Technical details</summary>
                                    <Data>{JSON.stringify({ at, source, event, details }, null, 2)}</Data>
                                </Disclosure>
                            </li>)}
                        </EventList>}
                    </Section>}
                    {view === 'technical' && <Section>
                        <h3>Technical information</h3>
                        <p>{sections.length > 0 ? 'These details are included when you copy diagnostics.' : 'No technical sections are available.'}</p>
                        {sections.map(({ id, title: sectionTitle, data }) => <Disclosure key={id}>
                            <summary>{sectionTitle}</summary>
                            <Data>{JSON.stringify(data, null, 2)}</Data>
                        </Disclosure>)}
                    </Section>}
                </Content>
            </DialogContent>
        </PanelDialog>
    </>;
}

internals.statusLabel = (status) => ({ success: 'Passed', error: 'Needs attention', pending: 'Waiting', unknown: 'Unknown' }[status] || 'Unknown');

internals.friendlyTitle = (event) => {

    const words = String(event || 'Event recorded').replace(/[._:-]+/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
};

internals.formatTime = (at) => {

    const date = new Date(at);
    return Number.isNaN(date.getTime()) ? String(at) : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

internals.statusColor = (status) => ({ success: '#21643c', error: '#a12622', pending: '#765600', unknown: '#455468' }[status] || '#455468');

internals.copyText = async (text, host) => {

    if (typeof navigator.clipboard?.writeText === 'function') {
        try {
            await navigator.clipboard.writeText(text);
            return;
        }
        catch (error) {
            // WebViews may expose this API without permitting clipboard access.
        }
    }

    const clipboard = window.cordova?.plugins?.clipboard || window.plugins?.clipboard;
    if (typeof clipboard?.copy === 'function') {
        try {
            await new Promise((resolve, reject) => clipboard.copy(text, resolve, reject));
            return;
        }
        catch (error) {
            // Retain the selection-based fallback if the native bridge fails.
        }
    }

    const previousFocus = document.activeElement;
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.readOnly = true;
    textarea.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;font-size:16px;';
    (host || document.body).appendChild(textarea);
    try {
        textarea.focus();
        textarea.select();
        textarea.setSelectionRange(0, textarea.value.length);
        if (!document.execCommand?.('copy')) {
            throw new Error('Clipboard access is unavailable.');
        }
    }
    finally {
        textarea.remove();
        previousFocus?.focus();
    }
};

internals.Launcher = Styled(Button)`
    && {
        position: fixed;
        right: max(16px, env(safe-area-inset-right, 0px));
        bottom: calc(88px + env(safe-area-inset-bottom, 0px));
        z-index: 19000;
    }
`;

internals.PanelDialog = Styled(Dialog)`
    && { z-index: 20000; }
    .MuiDialog-paper {
        max-height: calc(100% - 48px);
        overflow: hidden;
        border-radius: 16px;
    }
    .MuiDialogContent-root {
        padding: 24px;
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
    }
    @media (max-width: 600px) {
        .MuiDialog-paper {
            margin: 0;
            width: 100%;
            max-width: 100%;
            height: 100%;
            max-height: 100%;
            border-radius: 0;
        }
        .MuiDialogContent-root {
            padding: 20px max(16px, env(safe-area-inset-right, 0px))
                max(24px, env(safe-area-inset-bottom, 0px)) max(16px, env(safe-area-inset-left, 0px));
        }
    }
`;

internals.Header = Styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-shrink: 0;
    padding: env(safe-area-inset-top, 0px) max(16px, env(safe-area-inset-right, 0px)) 0 max(0px, env(safe-area-inset-left, 0px));
    h2 { min-width: 0; overflow-wrap: anywhere; font-weight: 700; }
`;

internals.Toolbar = Styled.div`
    flex-shrink: 0;
    padding: 0 max(20px, env(safe-area-inset-right, 0px)) 16px max(20px, env(safe-area-inset-left, 0px));
    p { margin: 12px 0 0; overflow-wrap: anywhere; font-size: 14px; }
`;

internals.Controls = Styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    button { min-height: 44px; text-transform: none; }
`;

internals.ViewNavigation = Styled.nav`
    display: flex;
    gap: 4px;
    margin-top: 12px;
    padding: 4px;
    border-radius: 10px;
    background: #edf1f6;
    button {
        flex: 1;
        min-width: 0;
        min-height: 40px;
        padding: 8px 4px;
        border: 0;
        border-radius: 7px;
        font: inherit;
        font-size: 13px;
        color: #455468;
        background: transparent;
        cursor: pointer;
    }
    button[aria-pressed='true'] {
        background: #fff;
        color: #163e68;
        font-weight: 700;
        box-shadow: 0 1px 4px #142e4a26;
    }
    button:focus-visible { outline: 2px solid #163e68; outline-offset: 1px; }
`;

internals.Search = Styled.label`
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 14px;
    input {
        width: 100%;
        min-width: 0;
        min-height: 44px;
        box-sizing: border-box;
        padding: 10px 12px;
        border: 1px solid #aab8c9;
        border-radius: 8px;
        font: inherit;
        font-size: 16px;
        background: inherit;
        color: inherit;
    }
`;

internals.Content = Styled.div`
    display: flex;
    flex-direction: column;
    gap: 28px;
    min-width: 0;
    font-size: 15px;
    line-height: 1.5;
    p, h3, h4 { margin: 0; overflow-wrap: anywhere; }
    h3 { font-size: 18px; line-height: 1.35; }
    h4 { font-size: 15px; line-height: 1.4; }
    ol { margin: 0; padding-left: 24px; }
    li + li { margin-top: 8px; }
`;

internals.ResultCard = Styled.section`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    padding: 20px;
    background: #f5f7fa;
    color: #223043;
    border-radius: 12px;
    border-left: 4px solid ${({ $status }) => {

        const { statusColor } = internals;
        return statusColor($status);
    }};
    h3 { font-size: 21px; }
`;

internals.Badge = Styled.span`
    display: inline-flex;
    align-self: flex-start;
    align-items: center;
    padding: 3px 10px;
    border-radius: 20px;
    background: #fff;
    color: ${({ $status }) => {

        const { statusColor } = internals;
        return statusColor($status);
    }};
    border: 1px solid currentColor;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
`;

internals.CheckList = Styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

internals.Check = Styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    border: 1px solid #d6dde6;
    border-radius: 10px;
`;

internals.EventHeading = Styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px 12px;
    time, > span:not([class]) { font-size: 12px; opacity: 0.75; }
`;

internals.Disclosure = Styled.details`
    min-width: 0;
    summary {
        cursor: pointer;
        min-height: 44px;
        padding: 10px 0;
        font-size: 14px;
        overflow-wrap: anywhere;
    }
    summary:focus-visible { outline: 2px solid currentColor; outline-offset: 3px; border-radius: 3px; }
`;

internals.Section = Styled.section`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
`;

internals.Data = Styled.pre`
    margin: 0;
    padding: 12px;
    max-width: 100%;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    border: 1px solid currentColor;
    border-radius: 4px;
    font-size: 12px;
`;

internals.EventList = Styled.ol`
    display: flex;
    flex-direction: column;
    gap: 0;
    margin: 0;
    padding: 0 !important;
    list-style: none;
    > li {
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-width: 0;
        padding: 16px 0;
        border-bottom: 1px solid #d6dde6;
    }
    > li:first-child { padding-top: 0; }
    > li:last-child { border-bottom: 0; }
`;
